import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Send, X } from "lucide-react";
import { toast } from "sonner";
import { API, FLOATING_NAV, INQUIRY_ENDPOINT, INQUIRY_OPTIONS, MARK_CIRCLE, PHONE_DISPLAY, PHONE_LINK, EMAIL, EMAIL_LINK, BUSINESS, STARTERS, STARTER_OPEN_FORM, scrollToId, openInquiry, slug, EASE } from "@/lib/site";

export const FloatingNav = () => {
  const [active, setActive] = useState("home");
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
    }, { rootMargin: "-40% 0px -55% 0px" });
    FLOATING_NAV.forEach((n) => { const el = document.getElementById(n.id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, []);
  return (
    <motion.nav
      data-testid="floating-nav"
      initial={{ y: 90, opacity: 0, x: "-50%" }}
      animate={{ y: 0, opacity: 1, x: "-50%" }}
      transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
      className="fixed bottom-4 sm:bottom-6 left-1/2 z-50 flex items-center gap-0.5 rounded-full border border-white/60 bg-white/45 backdrop-blur-2xl backdrop-saturate-150 px-1.5 py-1.5 overflow-hidden shadow-[0_18px_50px_rgba(17,17,17,0.14),0_2px_8px_rgba(17,17,17,0.06),inset_0_1px_0_rgba(255,255,255,0.85)]"
      aria-label="Primary"
    >
      <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/50 via-white/5 to-white/10" aria-hidden="true" />
      {FLOATING_NAV.map((n) => (
        <button
          key={n.id}
          data-testid={`nav-link-${n.id}`}
          onClick={() => scrollToId(n.id)}
          aria-current={active === n.id ? "true" : undefined}
          className={`relative px-2 sm:px-4 py-2 text-[10px] sm:text-[11px] font-semibold tracking-[0.04em] sm:tracking-[0.14em] transition-colors duration-300 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf ${active === n.id ? "text-cream" : "text-charcoal/75 hover:text-charcoal"}`}
        >
          {active === n.id && <motion.span layoutId="nav-active-pill" className="absolute inset-0 bg-forest rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
          <span className="relative z-10">{n.label}</span>
        </button>
      ))}
    </motion.nav>
  );
};

export const CallButton = () => (
  <motion.a
    data-testid="call-phil-gray-floating-button"
    href={PHONE_LINK}
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ delay: 1.5, type: "spring", stiffness: 260, damping: 20 }}
    className="group fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-50 h-12 flex items-center rounded-full overflow-hidden shadow-[0_10px_30px_rgba(0,85,29,0.35)] hover:shadow-[0_14px_40px_rgba(0,85,29,0.45)] transition-shadow duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun focus-visible:ring-offset-2"
    aria-label={`Call ${BUSINESS} at ${PHONE_DISPLAY}`}
  >
    <span className="texture-green absolute inset-0 transition-transform duration-500 group-hover:scale-110" />
    <span className="relative z-10 max-w-0 group-hover:max-w-[240px] transition-[max-width,padding] duration-500 overflow-hidden whitespace-nowrap text-white text-xs font-bold tracking-[0.1em] group-hover:pl-5">{PHONE_DISPLAY}</span>
    <span className="relative z-10 w-12 h-12 shrink-0 flex items-center justify-center text-sun">
      <Phone className="w-5 h-5 transition-transform duration-500 group-hover:rotate-12" />
    </span>
  </motion.a>
);

const validPhone = (v) => {
  const digits = v.replace(/\D/g, "");
  return /^\+?[0-9\s\-().]+$/.test(v.trim()) && digits.length >= 7 && digits.length <= 15;
};
const PHONE_ERR = "Enter a valid phone number with 7 to 15 digits.";
const GREETING = "Hi! Welcome to Phil Gray Landscaping. I can help you explore our services and get started with a project inquiry. What would you like to change about your yard?";
const SESSION_KEY = "pgl-assistant-session";

const LeadForm = ({ onReady }) => {
  const [lead, setLead] = useState({ name: "", phone: "", email: "" });
  const [error, setError] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [busy, setBusy] = useState(false);
  const field = "w-full rounded-xl border border-charcoal/15 bg-white/80 px-4 py-3 text-sm outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/25 transition-[border-color,box-shadow]";
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!lead.name.trim() || !lead.phone.trim() || !lead.email.trim()) { setError("Please complete all three fields."); return; }
    if (!validPhone(lead.phone)) { setPhoneErr(PHONE_ERR); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/leads`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(lead) });
      const data = await res.json();
      if (!res.ok || !data?.session_id) throw new Error("lead failed");
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ sessionId: data.session_id, name: lead.name }));
      onReady(data.session_id, lead.name);
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="flex-1 flex flex-col justify-center px-6 gap-3.5 overflow-y-auto" data-lenis-prevent>
      <p className="font-display font-medium text-xl text-charcoal tracking-tight">Before we start</p>
      <p className="text-sm text-charcoal/70 -mt-2 mb-2">Share your details so the team can follow up about your project.</p>
      <input data-testid="ai-lead-form-name-input" aria-label="Name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Name" className={field} />
      <input data-testid="ai-lead-form-email-input" aria-label="Email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email" type="email" className={field} />
      <div>
        <input
          data-testid="ai-lead-form-phone-input"
          aria-label="Phone"
          value={lead.phone}
          onChange={(e) => { setLead({ ...lead, phone: e.target.value }); setPhoneErr(""); }}
          onBlur={() => { if (lead.phone.trim() && !validPhone(lead.phone)) setPhoneErr(PHONE_ERR); }}
          placeholder="Phone"
          type="tel"
          className={`${field} ${phoneErr ? "border-red-600" : ""}`}
        />
        {phoneErr && <p data-testid="ai-lead-form-phone-error" className="mt-1.5 text-xs text-red-700 leading-snug">{phoneErr}</p>}
      </div>
      {error && <p data-testid="ai-lead-form-error" className="text-xs text-red-700">{error}</p>}
      <button data-testid="ai-lead-form-submit-button" type="submit" disabled={busy} className="btn-sun mt-1 rounded-xl py-3.5 text-sm">
        <span className="btn-sun-fill" aria-hidden="true" />
        <span className="relative z-10">{busy ? "ONE MOMENT…" : "START CHATTING"}</span>
      </button>
    </form>
  );
};

const readStream = async (res, onToken) => {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let failed = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const part of parts) {
      if (!part.startsWith("data:")) continue;
      const payload = part.slice(5).trim();
      if (payload === "[DONE]") continue;
      try {
        const data = JSON.parse(payload);
        if (data?.error) failed = true;
        if (data?.token) onToken(data.token);
      } catch { /* ignore partial frames */ }
    }
  }
  return !failed;
};

export const Assistant = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (!saved) return;
    try {
      const { sessionId: id, name } = JSON.parse(saved);
      setSessionId(id);
      setMessages([{ role: "assistant", text: `Welcome back, ${String(name).split(" ")[0]}. What would you like to change about your yard?` }]);
    } catch { sessionStorage.removeItem(SESSION_KEY); }
  }, []);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const setLast = (fn) => setMessages((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy || !sessionId) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId, message: msg }) });
      if (res.status === 404) { sessionStorage.removeItem(SESSION_KEY); setSessionId(null); throw new Error("expired"); }
      if (!res.ok || !res.body) throw new Error("chat failed");
      const ok = await readStream(res, (tok) => setLast((l) => ({ ...l, text: l.text + tok })));
      if (!ok) throw new Error("stream failed");
    } catch {
      setLast((l) => ({ ...l, text: l.text || `Something went wrong. Please try again, or call ${PHONE_DISPLAY} / email ${EMAIL}.` }));
    }
    setBusy(false);
  };

  const onStarter = (s) => {
    if (s === STARTER_OPEN_FORM) { setOpen(false); openInquiry(); } else send(s);
  };

  return (
    <>
      <motion.button
        data-testid="ask-phil-gray-floating-button"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 260, damping: 20 }}
        onClick={() => setOpen(true)}
        className="group fixed bottom-20 left-4 sm:bottom-6 sm:left-6 z-50 flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2"
        aria-label="Ask the Phil Gray Landscaping assistant"
      >
        <span className="w-12 h-12 shrink-0 rounded-full overflow-hidden shadow-[0_10px_30px_rgba(1,119,41,0.4)] transition-transform duration-300 group-hover:scale-105">
          <img src={MARK_CIRCLE} alt="" className="w-full h-full object-cover" />
        </span>
        <span className="max-w-0 group-hover:max-w-[170px] group-focus-visible:max-w-[170px] overflow-hidden transition-[max-width] duration-500 whitespace-nowrap">
          <span className="ml-2 relative inline-block overflow-hidden rounded-full text-charcoal text-xs font-bold tracking-[0.1em] px-5 py-3.5 shadow-[0_8px_24px_rgba(150,120,0,0.3)]">
            <span className="texture-sun absolute inset-0" aria-hidden="true" />
            <span className="relative">ASK PHIL GRAY</span>
          </span>
        </span>
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-panel"
            role="dialog"
            aria-label="Phil Gray Landscaping assistant"
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="fixed bottom-36 sm:bottom-24 left-4 sm:left-6 z-50 w-[min(400px,calc(100vw-2rem))] h-[540px] max-h-[72vh] rounded-3xl bg-white/85 backdrop-blur-2xl border border-charcoal/10 shadow-[0_24px_80px_rgba(17,17,17,0.25)] flex flex-col overflow-hidden origin-bottom-left"
            data-lenis-prevent
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-charcoal/[0.07]">
              <div className="flex items-center gap-3">
                <img src={MARK_CIRCLE} alt="Phil Gray Landscaping mark" className="w-9 h-9 rounded-full object-cover shrink-0" />
                <div>
                  <p className="font-display font-semibold text-sm text-charcoal tracking-tight">Phil Gray Landscaping</p>
                  <p className="font-mono text-[11px] tracking-[0.16em] text-leaf">PROJECT ASSISTANT</p>
                </div>
              </div>
              <button data-testid="ai-panel-close-button" onClick={() => setOpen(false)} aria-label="Close assistant" className="w-8 h-8 rounded-full hover:bg-charcoal/5 flex items-center justify-center text-charcoal/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf">
                <X className="w-4 h-4" />
              </button>
            </div>
            {sessionId ? (
              <>
                <div ref={listRef} data-testid="ai-chat-messages" data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-3" style={{ touchAction: "pan-y" }}>
                  {(messages.length ? messages : [{ role: "assistant", text: GREETING }]).map((m, i, arr) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-forest text-cream rounded-br-md" : "bg-charcoal/[0.05] text-charcoal rounded-bl-md"}`}>
                        {m.text}
                        {m.role === "assistant" && busy && i === arr.length - 1 && !m.text && <span className="inline-block w-1.5 h-3.5 bg-leaf animate-pulse" />}
                      </div>
                    </div>
                  ))}
                  {messages.length <= 1 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {STARTERS.map((s) => (
                        <button key={s} data-testid={`ai-starter-${slug(s)}`} onClick={() => onStarter(s)} className="rounded-full border border-leaf/30 px-3.5 py-2 text-xs text-charcoal/80 hover:border-leaf hover:bg-leaf/5 hover:text-leaf transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf">
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-charcoal/[0.07]">
                  <div className="flex items-center gap-2 rounded-full border border-charcoal/15 bg-white/80 pl-4 pr-1.5 py-1.5 focus-within:border-leaf focus-within:ring-2 focus-within:ring-leaf/25 transition-[border-color,box-shadow]">
                    <input data-testid="ai-chat-input" aria-label="Message" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about landscaping, patios, planting…" className="flex-1 min-w-0 bg-transparent text-sm outline-none" />
                    <button data-testid="ai-chat-send-button" onClick={() => send()} disabled={busy || !input.trim()} aria-label="Send message" className="btn-sun w-9 h-9 rounded-full shrink-0">
                      <span className="btn-sun-fill" aria-hidden="true" />
                      <Send className="relative z-10 w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <LeadForm onReady={(id) => { setSessionId(id); setMessages([{ role: "assistant", text: GREETING }]); }} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const EMPTY = { name: "", city_zip: "", email: "", phone: "", project_type: "", message: "" };

export const InquiryModal = ({ open, service, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const connected = Boolean(INQUIRY_ENDPOINT);
  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, project_type: INQUIRY_OPTIONS.includes(service) ? service : "" }));
  }, [open, service]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => { window.removeEventListener("keydown", onKey); window.__lenis?.start(); };
  }, [open, onClose]);
  const field = "w-full rounded-xl border border-charcoal/15 bg-white px-4 py-3 text-sm outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/25 transition-[border-color,box-shadow]";
  const submit = async (e) => {
    e.preventDefault();
    if (!connected) return;
    setSending(true);
    try {
      const res = await fetch(INQUIRY_ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error("send failed");
      toast.success("Thank you. Your inquiry has been sent to Phil Gray Landscaping.");
      setForm(EMPTY);
      onClose();
    } catch {
      toast.error(`Your inquiry could not be sent. Please try again, email ${EMAIL}, or call ${PHONE_DISPLAY}.`);
    }
    setSending(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="inquiry-modal" role="dialog" aria-modal="true" aria-labelledby="inquiry-title" className="fixed inset-0 z-[80] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-charcoal/55 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 34, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="relative w-full max-w-lg bg-cream rounded-[1.75rem] p-6 sm:p-10 shadow-2xl max-h-[90vh] overflow-y-auto"
            data-lenis-prevent
          >
            <button data-testid="inquiry-modal-close-button" onClick={onClose} aria-label="Close project inquiry form" className="absolute top-5 right-5 w-9 h-9 rounded-full hover:bg-charcoal/5 flex items-center justify-center text-charcoal/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf">
              <X className="w-4 h-4" />
            </button>
            <p className="eyebrow mb-2 pr-10">CONTACT PHIL GRAY LANDSCAPING</p>
            <h3 id="inquiry-title" className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-charcoal">Let's talk about your yard.</h3>
            <p className="mt-3 mb-7 text-sm text-charcoal/75 leading-relaxed">
              Tell us where your property is and what you would like to change. Whether you are considering one improvement or a full redesign, share the ideas you have.
            </p>
            <form onSubmit={submit} className="space-y-3.5">
              <div className="grid sm:grid-cols-2 gap-3.5">
                <input data-testid="inquiry-name-input" aria-label="Full name" required value={form.name} onChange={set("name")} placeholder="Full name *" className={field} />
                <input data-testid="inquiry-city-input" aria-label="City or ZIP code" value={form.city_zip} onChange={set("city_zip")} placeholder="City or ZIP code" className={field} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                <input data-testid="inquiry-email-input" aria-label="Email address" required type="email" value={form.email} onChange={set("email")} placeholder="Email address *" className={field} />
                <input data-testid="inquiry-phone-input" aria-label="Phone (optional)" type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className={field} />
              </div>
              <select data-testid="inquiry-type-select" aria-label="Project of interest" value={form.project_type} onChange={set("project_type")} className={`${field} text-charcoal/85`}>
                <option value="" disabled>Project of interest</option>
                {INQUIRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <div>
                <label htmlFor="inquiry-message" className="block mb-1.5 text-xs font-semibold text-charcoal/85">Tell us about your yard</label>
                <textarea id="inquiry-message" data-testid="inquiry-message-input" value={form.message} onChange={set("message")} rows={5} className={`${field} resize-none`}
                  placeholder="What would you like to change? Include your location, the area you want to improve, and any ideas, budget considerations, or preferred timing." />
              </div>
              {!connected && (
                <p data-testid="inquiry-offline-notice" role="status" className="rounded-xl border border-leaf/30 bg-leaf/[0.06] px-4 py-3 text-xs text-charcoal/85 leading-relaxed">
                  Online inquiries are not connected yet. Please call <a href={PHONE_LINK} className="font-semibold text-leaf underline underline-offset-2">{PHONE_DISPLAY}</a> or email <a href={EMAIL_LINK} className="font-semibold text-leaf underline underline-offset-2">{EMAIL}</a>.
                </p>
              )}
              <button data-testid="inquiry-submit-button" type="submit" disabled={sending || !connected} className="btn-sun w-full rounded-xl py-4 text-sm">
                <span className="btn-sun-fill" aria-hidden="true" />
                <span className="relative z-10">{sending ? "SENDING…" : "SEND MY INQUIRY →"}</span>
              </button>
              <p className="text-center text-xs text-charcoal/75">
                Prefer to talk? Call <a href={PHONE_LINK} className="text-leaf font-semibold hover:underline">{PHONE_DISPLAY}</a> or email <a href={EMAIL_LINK} className="text-leaf font-semibold hover:underline">{EMAIL}</a>.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
